<template>
  <div class="streams-schedule__nav">
    <div class="streams-schedule__nav-prev">
      <i class="fa-solid fa-chevron-left"></i>
    </div>
    <Swiper class-name="streams-schedule__nav-items" :swiper-options="swiperOptions">
      <div
        v-for="(navItem, navIndex) in nav"
        :key="navIndex"
        class="swiper-slide"
        :class="{ 'swiper-slide-selected': navIndex === selected }"
        @click.prevent="handleSetDate(navItem.date, navIndex)"
      >
        {{ navItem.title }}
      </div>
    </Swiper>
    <div class="streams-schedule__nav-next">
      <i class="fa-solid fa-chevron-right"></i>
    </div>
  </div>
</template>
<script lang="ts">
import Vue from 'vue';
import dayjs from 'dayjs';
import Swiper from '@custom/components/common/Swiper.vue';

interface Props {
  date: string;
}

interface Data {
  selected: number;
  swiperOptions: Record<string, any>;
}

interface Computed {
  nav: Record<string, any>;
}

interface Methods {
  handleSetDate: (date: string, index: number) => void;
}

export type NavItem = {
  date: string;
  title: string;
};

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    Swiper,
  },
  props: {
    date: {
      type: String,
      default: '',
      required: true,
    },
  },
  data() {
    return {
      selected: 7,
      swiperOptions: {
        spaceBetween: 6,
        initialSlide: 7,
        loop: false,
        autoplay: false,
        freeMode: {
          enabled: true,
          sticky: true,
        },
        slidesPerView: 1.5,
        slidesPerGroup: 1,
        pagination: {
          enabled: false,
        },
        navigation: {
          nextEl: '.streams-schedule__nav-next',
          prevEl: '.streams-schedule__nav-prev',
          enabled: false,
        },
        scrollbar: {
          enabled: false,
        },
        simulateTouch: false,
        breakpoints: {
          768: {
            freeMode: {
              enabled: false,
            },
            initialSlide: 8,
            slidesPerView: 3,
            slidesPerGroup: 2,
            loop: false,
            navigation: {
              enabled: true,
            },
          },
          1200: {
            freeMode: {
              enabled: false,
            },
            initialSlide: 8,
            slidesPerView: 6,
            slidesPerGroup: 2,
            loop: false,
            navigation: {
              enabled: true,
            },
          },
        },
      },
    };
  },
  computed: {
    nav() {
      const nav: NavItem[] = [];

      const today = dayjs();
      const tomorrow = today.add(1, 'day');
      const plusOne = tomorrow.add(1, 'day');

      for (let d = 7; d > 0; d--) {
        const date = today.subtract(d, 'day');
        nav.push({ date: date.format('YYYY-MM-DD'), title: `${date.format('dddd, MM DD')}` });
      }

      nav.push({ date: today.format('YYYY-MM-DD'), title: `Šiandien, ${today.format('MM DD')}` });
      nav.push({ date: tomorrow.format('YYYY-MM-DD'), title: `Rytoj, ${tomorrow.format('MM DD')}` });
      nav.push({ date: plusOne.format('YYYY-MM-DD'), title: `${plusOne.format('dddd, MM DD')}` });

      return nav;
    },
  },
  created() {
    dayjs.locale('lt');
  },
  methods: {
    handleSetDate(date, index) {
      this.selected = index;
      this.$emit('set-date', date);
    },
  },
});
</script>
<style lang="scss">
.streams-schedule {
  &__nav {
    position: relative;
    margin-bottom: 20px;

    .swiper-slide {
      padding: 20px;
      color: #708597;
      font-size: 12px;
      font-weight: 500;
      line-height: 12px;
      text-align: center;
      text-transform: capitalize;
      background: #eff2f4;
      border-radius: 6px;
      cursor: pointer;
      z-index: 1;

      &-selected {
        color: #fff;
        background-color: #708597;
      }
    }

    &-prev,
    &-next {
      position: absolute;
      top: 8px;
      width: 36px;
      height: 36px;
      text-align: center;
      background: #fff;
      border-radius: 50%;
      box-shadow: 0 2px 8px #00000026;
      cursor: pointer;
      z-index: 10;

      @include media-breakpoint-down(xs) {
        display: none;
      }

      i {
        color: #3858f5;
        font-size: 10px;
        line-height: 36px;
      }
    }

    &-prev {
      left: -18px;
    }

    &-next {
      right: -18px;
    }

    .swiper-button-disabled {
      &::after {
        opacity: 0.5;
      }
    }

    .swiper-button-prev,
    .swiper-button-next {
      display: none;
    }
  }
}
</style>
